import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { LuEye } from "react-icons/lu";
import { FiFilter } from "react-icons/fi";
import { BiExport } from "react-icons/bi";

import Badge from "../../../components/badge/badge";
import InputSearch from "../../../components/inputs/inputSearch/inputSearch";
import Button from "../../../components/button/button";
import Notification from "../../../components/notification/notification";
import Modal from "../../../components/modal/modal";
import Accordion from "../../../components/accordion/accordion";
import Tag from "../../../components/tag/tag";
import { getApiInstance } from "../../../utils/api";
import Table from "../../../components/table/table";
import SectionHeader from "../../../components/sectionHeader/sectionHeader";
import { matchesSearchInObject } from "../../../utils/searchUtils";

const familiaMapping = {
    1: "Mobiliario Central",
    2: "Mobiliario Perimetral",
    3: "Mesas",
    4: "Probadores",
    5: "Caja",
    6: "Dotación",
    7: "Instalaciones",
    8: "Señalética",
    9: "BOH",
    10: "Detalles constructivos",
11: "Detalles instalaciones",
12: "Montaje de mobiliario",
};

const categoriasMapping = {
    TiendasTop: "Tiendas Top",
    TiendasLight: "Tiendas Light",
    TiendasLow: "Tiendas Low",
    General: "General",
    Banner: "Banners",
    Senalizacon: "Señalización",
    PositiveStore: "Positive Store",
    MakeUp: "Make Up 400",
};

const columns = [
    {
        key: "rutaImagen",
        label: "Imagen",
        render: (row) => (
            <img
                src={row.rutaImagen || "defaultImage.png"}
                alt={row.nombreBook || "Sin nombre"}
                style={{
                    maxWidth: "48px",
                    maxHeight: "48px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                }}
            />
        ),
    },
    { key: "nombreBook", label: "Nombre" },
    { key: "referencia", label: "Referencia" },
    {
        key: "variante",
        label: "Variante",
        render: (row) => row.variante || "N/A",
    },
    {
        key: "nombreFamilia",
        label: "Categoría",
        render: (row) => familiaMapping[row.codFamilia] || "N/A",
    },
    // {
    //     key: "categorias",
    //     label: "Subcategorías",
    //     render: (row) => {
    //         if (Array.isArray(row.categorias) && row.categorias.length > 0) {
    //             return row.categorias
    //                 .map(
    //                     (categoria) => categoriasMapping[categoria] || categoria
    //                 )
    //                 .join(", ");
    //         } else {
    //             return "Sin categorías";
    //         }
    //     },
    // },
    {
        key: "estado",
        label: "Estado",
        render: (row) => {
            let variant = "danger";
            return <Badge label="Descatalogado" variant={variant} />;
        },
    },
];

export default function Descatalogados() {
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedMuebles, setSelectedMuebles] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [filterVariantes, setFilterVariantes] = useState([]);
    const [filterCategoria, setFilterCategorias] = useState([]);
    const [filterSubcategoria, setFilterSubcategoria] = useState([]);
    const [filterFamilia, setFilterFamilia] = useState([]);
    const [variantes, setVariantes] = useState([]);
    const [subCategorias, setSubcategorias] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [familias, setFamilias] = useState([]);
    const notificationRef = useRef();

    const api = getApiInstance();

    // Navegar a la ficha del mueble
   const handleVerCambios = (row) => {
        navigate(`/ficha-producto/${row.codMueble}`);
    };

    // Acciones por fila (mismo patrón que en Versions)
    const accionesDescatalogados = (row) => [
        {
            label: "Ver detalle",
            onClick: () => handleVerCambios(row),
            icon: <LuEye />,
        },
    ];

    const fetchProducts = async () => {
        try {
            const response = await api.get(
                "/api/Mueble/GetMueblesPublicadosDescatalogados"
            );
            console.log("Muebles descatalogados:", response.data);

            // Asegurarnos de que categorias siempre sea un array
            const processedProducts = (response.data || []).map((product) => ({
                ...product,
                categorias: Array.isArray(product.categorias)
                    ? product.categorias
                    : [],
            }));

            setProducts(processedProducts);
        } catch (error) {
            console.error("Error al obtener muebles descatalogados:", error);
            notificationRef.current.addNotification(
                "Error al obtener los muebles descatalogados.",
                "error"
            );
            setProducts([]);
        }
    };

    useEffect(() => {
        fetchProducts();
    }, []);

    useEffect(() => {
        if (products.length > 0) {
            const allVariantes = [
                ...new Set(products.map((p) => p.variante).filter(Boolean)),
            ];
            const allSubcategorias = [
                ...new Set(products.flatMap((p) => p.categorias || [])),
            ];
            const allCategorias = [
                ...new Set(
                    products
                        .map((p) => familiaMapping[p.codFamilia])
                        .filter(Boolean)
                ),
            ];
            const allFamilias = [
                ...new Set(
                    products.map((p) => p.nombreFamilia).filter(Boolean)
                ),
            ];

            setVariantes(allVariantes);
            setSubcategorias(allSubcategorias);
            setCategorias(allCategorias);
            setFamilias(allFamilias);
        }
    }, [products]);

    const filteredProducts = useMemo(() => {
        return products.filter((product) => {
            const matchesSearch =
                searchTerm === "" || matchesSearchInObject(product, searchTerm);

            const matchesVariantes =
                filterVariantes.length === 0 ||
                (product.variante &&
                    filterVariantes.includes(product.variante));

            const productCategoria = familiaMapping[product.codFamilia];
            const matchesCategoria =
                filterCategoria.length === 0 ||
                (productCategoria &&
                    filterCategoria.includes(productCategoria));

            const matchesSubcategoria =
                filterSubcategoria.length === 0 ||
                (product.categorias &&
                    product.categorias.some((c) =>
                        filterSubcategoria.includes(c)
                    ));

            const matchesFamilia =
                filterFamilia.length === 0 ||
                (product.nombreFamilia &&
                    filterFamilia.includes(product.nombreFamilia));

            return (
                matchesSearch &&
                matchesVariantes &&
                matchesCategoria &&
                matchesSubcategoria &&
                matchesFamilia
            );
        });
    }, [
        searchTerm,
        products,
        filterVariantes,
        filterCategoria,
        filterSubcategoria,
        filterFamilia,
    ]);

    const handleDownloadPlanosYExcelZip = async () => {
        if (selectedMuebles.length === 0) {
            notificationRef.current.addNotification(
                "Selecciona al menos un mueble para descargar planos y Excel.",
                "warning"
            );
            return;
        }

        try {
            const response = await api.post(
                "/api/Mueble/DownloadPlanosYExcelZip",
                selectedMuebles,
                {
                    responseType: "blob",
                }
            );

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
                "download",
                "PlanosYExcelMueblesDescatalogados.zip"
            );
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            notificationRef.current.addNotification(
                "Error al descargar los planos y el Excel.",
                "error"
            );
            console.error("Error al descargar los planos y el Excel:", error);
        }
    };

    const openModal = () => {
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    const handleAcceptFilters = () => {
        closeModal();
    };

    const handleCleanFilter = () => {
        setFilterVariantes([]);
        setFilterCategorias([]);
        setFilterSubcategoria([]);
        setFilterFamilia([]);
    };

    const handleSelectVariante = (varianteName) => {
        setFilterVariantes((prevSelected) => {
            if (prevSelected.includes(varianteName)) {
                return prevSelected.filter(
                    (variante) => variante !== varianteName
                );
            } else {
                return [...prevSelected, varianteName];
            }
        });
    };

    const handleSelectCategoria = (categoriaName) => {
        setFilterCategorias((prevSelected) => {
            if (prevSelected.includes(categoriaName)) {
                return prevSelected.filter(
                    (categoria) => categoria !== categoriaName
                );
            } else {
                return [...prevSelected, categoriaName];
            }
        });
    };

    const handleSelectSubcategoria = (subcategoriaName) => {
        setFilterSubcategoria((prevSelected) => {
            if (prevSelected.includes(subcategoriaName)) {
                return prevSelected.filter(
                    (subcategoria) => subcategoria !== subcategoriaName
                );
            } else {
                return [...prevSelected, subcategoriaName];
            }
        });
    };

    const handleSelectFamilia = (familiaName) => {
        setFilterFamilia((prevSelected) => {
            if (prevSelected.includes(familiaName)) {
                return prevSelected.filter(
                    (familia) => familia !== familiaName
                );
            } else {
                return [...prevSelected, familiaName];
            }
        });
    };

    const handleRowSelect = (selectedRows) => {
        setSelectedMuebles(selectedRows);
    };

    return (
        <div className="lista-muebles-container">
            <SectionHeader
                title="Muebles Descatalogados"
                subtitle="Listado de todos los muebles que han sido descatalogados"
            />

            <div className="dashboard-productos__content__table-header">
                <InputSearch
                    context="tableSearch"
                    placeholder="Buscar..."
                    onSearch={setSearchTerm}
                />

                <div className="dashboard-productos__content__table-header__buttons">
                    <Button
                        onClick={openModal}
                        label="Filtros"
                        icon={<FiFilter />}
                    />
                    <Button
                        label="Exportar"
                        variant="outline"
                        icon={<BiExport />}
                        onClick={handleDownloadPlanosYExcelZip}
                        disabled={selectedMuebles.length === 0}
                    />
                </div>
            </div>

            <Table
                data={filteredProducts}
                columns={columns}
                selectedItems={selectedMuebles}
                onRowSelect={handleRowSelect}
                actions={accionesDescatalogados}
                emptyStateMessage="No se encontraron muebles descatalogados"
            />

            <Notification ref={notificationRef} />

            <Modal
                title="Filtros"
                isOpen={isModalOpen}
                onClose={closeModal}
                onAccept={handleAcceptFilters}
                outlineButtonLabel="Limpiar"
                primaryButtonLabel={`Ver resultados (${filteredProducts.length})`}
                onCancel={handleCleanFilter}
            >
                <div className="section-header__modal__section">
                    <Accordion title="Variante">
                        <ul className="section-header__modal__section__content">
                            {variantes.map((variante, index) => (
                                <Tag
                                    key={index}
                                    label={variante}
                                    isSelected={filterVariantes.includes(
                                        variante
                                    )}
                                    onSelect={handleSelectVariante}
                                />
                            ))}
                        </ul>
                    </Accordion>
                </div>
                <div className="section-header__modal__section">
                    <Accordion title="Categoría">
                        <ul className="section-header__modal__section__content">
                            {categorias.map((categoria, index) => (
                                <Tag
                                    key={index}
                                    label={categoria}
                                    isSelected={filterCategoria.includes(
                                        categoria
                                    )}
                                    onSelect={handleSelectCategoria}
                                />
                            ))}
                        </ul>
                    </Accordion>
                </div>
                {/* <div className="section-header__modal__section">
                    <Accordion title="Subcategoría">
                        <ul className="section-header__modal__section__content">
                            {subCategorias.map((subcategoria, index) => (
                                <Tag
                                    key={index}
                                    label={subcategoria}
                                    isSelected={filterSubcategoria.includes(
                                        subcategoria
                                    )}
                                    onSelect={handleSelectSubcategoria}
                                />
                            ))}
                        </ul>
                    </Accordion>
                </div> */}
            </Modal>
        </div>
    );
}
